import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Apple, Plus, CheckCircle2, History, Archive } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Card, Button, Field, Input, Select, StatusBadge, Spinner, PageGuide } from "../../components/ui-kit";

const EVIDENCE_OPTIONS = [
  { value: "official_database", label: "Banca dati ufficiale (es. CREA, BDA)" },
  { value: "lab_analysis", label: "Analisi di laboratorio sul prodotto reale" },
  { value: "supplier_declaration", label: "Dichiarazione del fornitore" },
  { value: "expert_estimate", label: "Stima tecnica esperta" },
];

const NUMERIC_FIELDS = [
  ["kcal", "Energia (kcal)", "Valore energetico per 100 g di prodotto, in kilocalorie."],
  ["protein_g", "Proteine (g)", "Grammi di proteine per 100 g di prodotto."],
  ["fat_g", "Grassi (g)", "Grammi di grassi totali per 100 g di prodotto."],
  ["saturates_g", "di cui saturi (g)", "Grammi di acidi grassi saturi per 100 g, compresi nel totale grassi indicato sopra."],
  ["carbs_g", "Carboidrati (g)", "Grammi di carboidrati totali per 100 g di prodotto."],
  ["sugars_g", "di cui zuccheri (g)", "Grammi di zuccheri per 100 g, compresi nel totale carboidrati indicato sopra."],
  ["fiber_g", "Fibre (g)", "Grammi di fibra alimentare per 100 g di prodotto."],
  ["salt_g", "Sale (g)", "Grammi di sale per 100 g di prodotto (non il solo sodio)."],
];

const err = (e) => formatApiErrorDetail(e.response?.data?.detail);
const emptyForm = { name: "", source: "", evidence_level: "expert_estimate", notes: "",
  ...Object.fromEntries(NUMERIC_FIELDS.map(([key]) => [key, ""])) };

export default function NutritionAdmin() {
  const [foods, setFoods] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [versionsOf, setVersionsOf] = useState(null);

  const load = () => api.get("/admin/nutrition/foods").then(({ data }) => setFoods(data.foods)).catch((e) => toast.error(err(e)));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim() || form.kcal === "" || !form.source.trim()) {
      toast.error("Nome, energia (kcal) e fonte sono obbligatori"); return;
    }
    const payload = { ...form };
    NUMERIC_FIELDS.forEach(([key]) => { payload[key] = payload[key] === "" ? 0 : Number(payload[key]); });
    try {
      await api.post("/admin/nutrition/foods", payload);
      toast.success("Alimento di riferimento creato in bozza");
      setForm(emptyForm);
      load();
    } catch (e) { toast.error(err(e)); }
  };

  const publish = async (id) => {
    try { await api.post(`/admin/nutrition/foods/${id}/publish`); toast.success("Pubblicato"); load(); }
    catch (e) { toast.error(err(e)); }
  };

  const archive = async (id) => {
    try { await api.patch(`/admin/nutrition/foods/${id}/archive`); toast.success("Archiviato"); load(); }
    catch (e) { toast.error(err(e)); }
  };

  const showVersions = async (food) => {
    try { const { data } = await api.get(`/admin/nutrition/foods/${food.id}/versions`); setVersionsOf({ groupId: food.group_id, versions: data.versions }); }
    catch (e) { toast.error(err(e)); }
  };

  if (foods === null) return <Spinner />;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-xl bg-navy text-gold-bright"><Apple className="h-5 w-5" /></div>
        <div>
          <h1 className="font-heading text-xl font-bold text-navy">DF Food Safety Expert Engine — Nutritional Analysis</h1>
          <p className="text-sm text-slate-500">Catalogo globale degli alimenti di riferimento (valori per 100 g), condiviso da tutti i clienti e versionato come le regole Shelf Life.</p>
        </div>
      </div>

      <PageGuide
        title="Guida rapida: Nutritional Analysis"
        summary="Questo catalogo condiviso di alimenti di riferimento alimenta l’analisi nutrizionale disponibile ai clienti: più è completo e accurato, più affidabili sono i valori che vedranno."
        goals={[
          "Costruire un catalogo condiviso di alimenti di riferimento con i valori nutrizionali per 100 g, riutilizzabile da tutti i clienti.",
          "Mantenere una versione tracciata di ogni alimento, così puoi correggere un valore senza perdere lo storico.",
        ]}
        steps={[
          "Inserisci nome, fonte del dato e valori nutrizionali per 100 g (l’energia in kcal è obbligatoria).",
          "Scegli il livello di evidenza più corretto: un dato da banca dati ufficiale o analisi di laboratorio è più affidabile di una stima esperta.",
          "L’alimento nasce in bozza: verificalo e premi “Pubblica” per renderlo disponibile ai clienti.",
          "Per correggere un errore in un alimento già pubblicato, archivialo (resta consultabile nello storico, icona orologio, ma non più in uso) e creane uno nuovo con il valore corretto.",
        ]}
        testid="nutrition-guide"
      />

      <Card className="p-5">
        <h2 className="mb-3 font-heading text-base font-semibold text-navy">Nuovo alimento di riferimento (bozza)</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="Nome alimento *" help="Il nome dell’alimento come comparirà nel catalogo condiviso, es. “Farina di grano tenero tipo 00”. Usa un nome generico, non legato a un singolo fornitore."><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Farina di grano tenero tipo 00" /></Field>
          <Field label="Livello di evidenza" help="Da dove viene il dato nutrizionale: una banca dati ufficiale, un’analisi di laboratorio, la dichiarazione del fornitore o una stima esperta. Determina quanto è affidabile il valore.">
            <Select value={form.evidence_level} onChange={(e) => setForm({ ...form, evidence_level: e.target.value })}>
              {EVIDENCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          </Field>
          <Field label="Fonte *" hint="Citazione verificabile, es. CREA, etichetta fornitore, referto di laboratorio" help="La fonte esatta da cui provengono i valori inseriti, così da poterla verificare in futuro."><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} /></Field>
          {NUMERIC_FIELDS.map(([key, label, help]) => (
            <Field key={key} label={label + (key === "kcal" ? " *" : "")} help={help}><Input type="number" step="0.1" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} /></Field>
          ))}
        </div>
        <div className="mt-4"><Field label="Note tecniche" help="Annotazioni interne per chi gestisce il catalogo, non visibili ai clienti."><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field></div>
        <div className="mt-4"><Button variant="success" onClick={submit}><Plus className="w-4 h-4" /> Crea in bozza</Button></div>
      </Card>

      <Card className="p-5">
        <h2 className="mb-3 font-heading text-base font-semibold text-navy">Alimenti di riferimento</h2>
        <div className="space-y-2">
          {foods.map((f) => (
            <div key={f.id} className="rounded-lg border border-hairline p-3 text-sm">
              <div className="min-w-0">
                <div className="font-medium text-navy break-words">{f.name} — v{f.version} — {f.kcal} kcal/100g</div>
                <div className="text-xs text-slate-400 break-words">{f.source} · {f.evidence_level}</div>
              </div>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <StatusBadge status={f.status === "pubblicata" ? "verde" : f.status === "bozza" ? "blu" : "grigio"}>{f.status}</StatusBadge>
                <div className="flex flex-wrap items-center gap-2">
                  {f.status !== "pubblicata" && <Button variant="outline" className="shrink-0" onClick={() => publish(f.id)}><CheckCircle2 className="w-4 h-4" /> Pubblica</Button>}
                  {f.status === "pubblicata" && <Button variant="ghost" className="shrink-0" onClick={() => archive(f.id)}><Archive className="w-4 h-4" /> Archivia</Button>}
                  <Button variant="ghost" className="shrink-0" onClick={() => showVersions(f)}><History className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          ))}
          {foods.length === 0 && <p className="text-sm text-slate-400">Nessun alimento di riferimento creato.</p>}
        </div>
      </Card>

      {versionsOf && (
        <Card className="p-5">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="font-heading text-sm font-semibold text-navy">Versioni dell'alimento</h3>
            <Button variant="ghost" onClick={() => setVersionsOf(null)}>Chiudi</Button>
          </div>
          {versionsOf.versions.map((v) => (
            <div key={v.id} className="flex items-center justify-between border-b border-hairline py-2 text-sm last:border-0">
              <span>v{v.version} — {v.kcal} kcal/100g</span>
              <StatusBadge status={v.status === "pubblicata" ? "verde" : v.status === "bozza" ? "blu" : "grigio"}>{v.status}</StatusBadge>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
